import { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  ShieldCheck, UserCheck, UserX, Clock, Users, Search, Ban, RotateCcw, Mail, UserCog
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateLong } from '../lib/dateFormat';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-500/15 text-amber-400 border-amber-500/20', icon: Clock },
  approved: { label: 'Active', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20', icon: UserCheck },
  declined: { label: 'Declined', color: 'bg-red-500/15 text-red-400 border-red-500/20', icon: UserX },
  deactivated: { label: 'Deactivated', color: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/20', icon: Ban },
};

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', desc: 'Full platform access to all features' },
  { value: 'manager', label: 'Manager', desc: 'Can manage content, team, integrations' },
  { value: 'team', label: 'Team', desc: 'View and update assigned tasks' },
  { value: 'owner', label: 'Owner', desc: 'Full access, gets own workspace' },
];

const ROLE_BADGE = {
  super_admin: 'bg-gold/15 text-gold border-gold/20',
  owner: 'bg-violet-500/15 text-violet-400 border-violet-500/20',
  admin: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  manager: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
  team: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
};

export default function AdminDashboardPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedRole, setSelectedRole] = useState('team');
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [changeRoleUser, setChangeRoleUser] = useState(null);
  const [newRole, setNewRole] = useState('team');
  const [counts, setCounts] = useState({ pending: 0, approved: 0, declined: 0, deactivated: 0 });

  const fetchUsers = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await adminAPI.listUsers(status === 'all' ? null : status);
      setUsers(res.data);
      const allRes = await adminAPI.listUsers();
      const c = { pending: 0, approved: 0, declined: 0, deactivated: 0 };
      allRes.data.forEach(u => { c[u.account_status] = (c[u.account_status] || 0) + 1; });
      setCounts(c);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const res = await adminAPI.listWorkspaces();
      setWorkspaces(res.data);
      if (res.data.length > 0) setSelectedWorkspace(res.data[0].id);
    } catch {}
  }, []);

  useEffect(() => { fetchUsers(tab); }, [tab, fetchUsers]);
  useEffect(() => { fetchWorkspaces(); }, [fetchWorkspaces]);

  const handleAction = async () => {
    if (!confirmAction) return;
    const { action, user } = confirmAction;
    try {
      if (action === 'approve') {
        await adminAPI.approve(user.id, {
          role: selectedRole,
          workspace_id: selectedRole !== 'owner' ? selectedWorkspace : null
        });
      }
      else if (action === 'decline') await adminAPI.decline(user.id);
      else if (action === 'deactivate') await adminAPI.deactivate(user.id);
      else if (action === 'reactivate') await adminAPI.reactivate(user.id);
      toast.success(`User ${action}d successfully`);
      setConfirmAction(null);
      setSelectedRole('team');
      fetchUsers(tab);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const handleChangeRole = async () => {
    if (!changeRoleUser) return;
    try {
      await adminAPI.changeRole(changeRoleUser.id, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      setChangeRoleUser(null);
      fetchUsers(tab);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change role');
    }
  };

  const filtered = users.filter(u =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6 animate-fade-in-up" data-testid="admin-dashboard-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-6 w-6 text-gold" />
          <h1 className="text-2xl font-display font-semibold">Admin Dashboard</h1>
        </div>
        <p className="text-sm text-muted-foreground">Manage user registrations, roles, and platform access.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Pending', count: counts.pending, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          { label: 'Active', count: counts.approved, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
          { label: 'Declined', count: counts.declined, color: 'text-red-400', bg: 'bg-red-500/10' },
          { label: 'Deactivated', count: counts.deactivated, color: 'text-zinc-400', bg: 'bg-zinc-500/10' },
        ].map((stat, i) => (
          <Card key={i} className="bg-card/60 backdrop-blur" data-testid={`stat-${stat.label.toLowerCase()}`}>
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-display font-bold ${stat.color}`}>{stat.count}</p>
              <p className="text-xs text-muted-foreground mt-1">{stat.label} Users</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList>
            <TabsTrigger value="pending" className="gap-1.5">
              <Clock className="h-3.5 w-3.5" /> Pending
              {counts.pending > 0 && <Badge className="ml-1 bg-amber-500/20 text-amber-400 text-[10px] px-1.5">{counts.pending}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="approved"><UserCheck className="h-3.5 w-3.5 mr-1.5" /> Active</TabsTrigger>
            <TabsTrigger value="declined"><UserX className="h-3.5 w-3.5 mr-1.5" /> Declined</TabsTrigger>
            <TabsTrigger value="deactivated"><Ban className="h-3.5 w-3.5 mr-1.5" /> Deactivated</TabsTrigger>
          </TabsList>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="admin-search-input" />
          </div>
        </div>

        {['pending', 'approved', 'declined', 'deactivated'].map(tabKey => (
          <TabsContent key={tabKey} value={tabKey} className="mt-4">
            {loading ? (
              <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-secondary rounded-xl animate-pulse" />)}</div>
            ) : filtered.length === 0 ? (
              <Card className="bg-card/60 backdrop-blur">
                <CardContent className="py-12 text-center">
                  <Users className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No {tabKey} users found.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filtered.map(user => {
                  const cfg = STATUS_CONFIG[user.account_status] || STATUS_CONFIG.pending;
                  const StatusIcon = cfg.icon;
                  const roleBadge = ROLE_BADGE[user.role] || ROLE_BADGE.editor;
                  return (
                    <Card key={user.id} className="bg-card/60 backdrop-blur" data-testid={`user-card-${user.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                              <span className="text-sm font-semibold text-primary">
                                {user.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium truncate">{user.name}</p>
                                {user.role === 'super_admin' && (
                                  <Badge className="bg-gold/15 text-gold border-gold/20 text-[10px]">
                                    <ShieldCheck className="h-2.5 w-2.5 mr-0.5" /> Super Admin
                                  </Badge>
                                )}
                                {user.role !== 'super_admin' && user.account_status === 'approved' && (
                                  <Badge className={`border text-[10px] ${roleBadge}`}>
                                    {user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                                <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {user.email}</span>
                                <span>Joined {formatDateLong(user.created_at)}</span>
                                {user.workspaces_count > 0 && <span>{user.workspaces_count} workspace(s)</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 flex-wrap">
                            <Badge className={`border text-[10px] ${cfg.color}`}>
                              <StatusIcon className="h-2.5 w-2.5 mr-1" /> {cfg.label}
                            </Badge>
                            {user.role !== 'super_admin' && (
                              <>
                                {user.account_status === 'pending' && (
                                  <>
                                    <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                                      onClick={() => { setSelectedRole('team'); setConfirmAction({ action: 'approve', user }); }}
                                      data-testid={`approve-${user.id}`}>
                                      <UserCheck className="h-3.5 w-3.5 mr-1" /> Approve
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-8 text-xs text-destructive"
                                      onClick={() => setConfirmAction({ action: 'decline', user })}
                                      data-testid={`decline-${user.id}`}>
                                      <UserX className="h-3.5 w-3.5 mr-1" /> Decline
                                    </Button>
                                  </>
                                )}
                                {user.account_status === 'approved' && (
                                  <>
                                    <Button size="sm" variant="outline" className="h-8 text-xs"
                                      onClick={() => { setNewRole(user.role || 'team'); setChangeRoleUser(user); }}
                                      data-testid={`change-role-${user.id}`}>
                                      <UserCog className="h-3.5 w-3.5 mr-1" /> Change Role
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-8 text-xs text-destructive"
                                      onClick={() => setConfirmAction({ action: 'deactivate', user })}
                                      data-testid={`deactivate-${user.id}`}>
                                      <Ban className="h-3.5 w-3.5 mr-1" /> Deactivate
                                    </Button>
                                  </>
                                )}
                                {(user.account_status === 'declined' || user.account_status === 'deactivated') && (
                                  <Button size="sm" variant="outline" className="h-8 text-xs"
                                    onClick={() => setConfirmAction({ action: 'reactivate', user })}
                                    data-testid={`reactivate-${user.id}`}>
                                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reactivate
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Approve Dialog with Role Selection */}
      <AlertDialog open={confirmAction?.action === 'approve'} onOpenChange={open => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve {confirmAction?.user?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Choose a role and workspace for this user. This determines what they can see and do on the platform.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Assign Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger data-testid="approve-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      <div>
                        <span className="font-medium">{r.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">— {r.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedRole !== 'owner' && workspaces.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Add to Workspace</Label>
                <Select value={selectedWorkspace} onValueChange={setSelectedWorkspace}>
                  <SelectTrigger data-testid="approve-workspace-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {workspaces.map(ws => (
                      <SelectItem key={ws.id} value={ws.id}>{ws.name} ({ws.member_count} members)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">The user will be added to this workspace with the selected role.</p>
              </div>
            )}
            {selectedRole === 'owner' && (
              <p className="text-xs text-amber-400 bg-amber-500/10 p-2 rounded">Owners get their own separate workspace with full control.</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAction} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-admin-action">
              Approve as {selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Other Actions Dialog (decline, deactivate, reactivate) */}
      <AlertDialog open={!!confirmAction && confirmAction?.action !== 'approve'} onOpenChange={open => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.action === 'decline' && 'Decline Registration?'}
              {confirmAction?.action === 'deactivate' && 'Deactivate User?'}
              {confirmAction?.action === 'reactivate' && 'Reactivate User?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.action === 'decline' && (<>This will decline <strong>{confirmAction?.user?.name}</strong>'s registration. They will not be able to log in.</>)}
              {confirmAction?.action === 'deactivate' && (<>This will deactivate <strong>{confirmAction?.user?.name}</strong>'s account. They will lose access immediately.</>)}
              {confirmAction?.action === 'reactivate' && (<>This will restore <strong>{confirmAction?.user?.name}</strong>'s access to the platform.</>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAction}
              className={confirmAction?.action === 'decline' || confirmAction?.action === 'deactivate'
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : 'bg-emerald-600 hover:bg-emerald-700'}
              data-testid="confirm-admin-action-other">
              {confirmAction?.action?.charAt(0).toUpperCase() + confirmAction?.action?.slice(1)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Role Dialog */}
      <AlertDialog open={!!changeRoleUser} onOpenChange={open => { if (!open) setChangeRoleUser(null); }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Change Role for {changeRoleUser?.name}</AlertDialogTitle>
            <AlertDialogDescription>
              Update this user's role. This changes what they can see and do across all their workspaces.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-sm font-medium mb-2 block">New Role</Label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger data-testid="change-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>
                    <span className="font-medium">{r.label}</span>
                    <span className="text-xs text-muted-foreground ml-2">— {r.desc}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleChangeRole} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-role-change">
              Update Role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
